import { useState, type FormEvent } from 'react';
import { useRessource, messageErreur } from '../../api/ressource';
import { api, ErreurApi } from '../../api/client';
import { useAuth } from '../../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../../composants/Tableau';
import { BandeauAlerte } from '../../composants/BandeauAlerte';
import { Champ } from '../../composants/Champ';
import { Pastille } from '../../composants/Pastille';
import { Modale } from '../../composants/Modale';
import { Icone } from '../../composants/Icone';

interface Indice {
  id: string;
  code: string;
  libelle: string;
  actif: boolean;
  /** Rattaché à au moins un benchmark de profil : son code est verrouillé. */
  utilise: boolean;
}

const VIDE = { code: '', libelle: '', actif: true };

export function Indices() {
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Indice[]>('/indices');
  const indices = donnees ?? [];
  const estAdmin = aRole('ADMIN');

  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(VIDE);
  const [codeVerrouille, setCodeVerrouille] = useState(false);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const enCreation = edition === '';

  function ouvrirCreation() {
    setSaisie(VIDE);
    setCodeVerrouille(false);
    setEdition('');
    setErreurForm(null);
  }
  function ouvrirEdition(i: Indice) {
    setSaisie({ code: i.code, libelle: i.libelle, actif: i.actif });
    setCodeVerrouille(i.utilise);
    setEdition(i.id);
    setErreurForm(null);
  }
  function fermer() {
    setEdition(null);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      if (enCreation) {
        await api.post('/indices', { code: saisie.code, libelle: saisie.libelle });
      } else {
        await api.patch(`/indices/${edition}`, {
          // Le code verrouillé n'est pas renvoyé : le serveur le refuserait.
          ...(codeVerrouille ? {} : { code: saisie.code }),
          libelle: saisie.libelle,
          actif: saisie.actif,
        });
      }
      fermer();
      await recharger();
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Indice>[] = [
    { cle: 'code', entete: 'Code', num: true, rendu: (i) => i.code },
    { cle: 'libelle', entete: 'Indice de référence', rendu: (i) => i.libelle },
    {
      cle: 'usage',
      entete: 'Usage',
      rendu: (i) =>
        i.utilise ? (
          <Pastille ton="interface">Référencé</Pastille>
        ) : (
          <Pastille ton="neutre">Libre</Pastille>
        ),
    },
    {
      cle: 'actif',
      entete: 'État',
      rendu: (i) =>
        i.actif ? (
          <Pastille ton="succes">Actif</Pastille>
        ) : (
          <Pastille ton="neutre">Inactif</Pastille>
        ),
    },
    ...(estAdmin
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (i: Indice) => (
              <button
                type="button"
                className="tableau__action"
                onClick={() => ouvrirEdition(i)}
                aria-label={`Éditer ${i.libelle}`}
              >
                <Icone nom="editer" taille={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Indices de référence</h1>
        <span className="entete__ref">{indices.length} indices</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Indices"
        meta={chargement ? 'chargement…' : undefined}
        actions={
          estAdmin && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouvel indice
            </button>
          )
        }
      >
        <Tableau colonnes={colonnes} lignes={indices} cle={(i) => i.id} vide="Aucun indice." />
      </Bloc>

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Indice de référence"
        titre={enCreation ? 'Nouvel indice' : 'Modifier l’indice'}
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <Champ
            label="Code"
            value={saisie.code}
            onChange={(e) => setSaisie({ ...saisie, code: e.target.value })}
            maxLength={20}
            required
            disabled={codeVerrouille}
            aide={
              codeVerrouille
                ? 'Verrouillé : cet indice sert de référence à un profil.'
                : 'Mis en majuscules. Verrouillé dès qu’un profil s’y réfère : il identifie les cours importés.'
            }
          />
          <Champ
            label="Libellé"
            value={saisie.libelle}
            onChange={(e) => setSaisie({ ...saisie, libelle: e.target.value })}
            maxLength={80}
            required
          />
          {!enCreation && (
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={saisie.actif}
                onChange={(e) => setSaisie({ ...saisie, actif: e.target.checked })}
              />
              Indice actif
            </label>
          )}
          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={occupe || !saisie.code.trim() || !saisie.libelle.trim()}
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer l’indice' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
